/**
 * Utility script to create a user document in Firestore that matches Firebase Auth
 * This helps resolve the "user not found in database" issue
 */

import { addDoc, collection } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Creates a user document in Firestore
 * @param {Object} userData - User data to create
 * @param {string} userData.firebase_uid - Firebase Auth UID (REQUIRED)
 * @param {string} userData.email - User email
 * @param {string} userData.name - User display name
 * @param {string} userData.role - User role (developer, school_admin, school_moderator, teacher)
 * @param {string} userData.school_id - School ID (for non-developer users)
 */
export const createUserInDatabase = async (userData) => {
  try {
    console.log('Creating user in database:', userData);
    
    // Validate required fields
    if (!userData.firebase_uid) {
      throw new Error('firebase_uid is required');
    }
    if (!userData.email) {
      throw new Error('email is required');
    }
    if (!userData.role) {
      throw new Error('role is required');
    }

    // Create the user document
    const userDoc = {
      firebase_uid: userData.firebase_uid,
      email: userData.email,
      name: userData.name || userData.email.split('@')[0],
      role: userData.role,
      active: true,
      created_at: new Date().toISOString(),
      permissions: getDefaultPermissions(userData.role),
      ...userData
    };

    // Add school-specific fields
    if (userData.role !== 'developer') {
      if (!userData.school_id) {
        throw new Error('school_id is required for non-developer users');
      }
    }

    const docRef = await addDoc(collection(db, 'users'), userDoc);
    console.log('User created with ID:', docRef.id);
    
    return { id: docRef.id, ...userDoc };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Get default permissions based on role
 */
const getDefaultPermissions = (role) => {
  switch (role) {
    case 'developer':
      return {
        all_schools_access: true,
        create_schools: true,
        delete_schools: true,
        manage_school_admins: true,
        view_all_analytics: true,
        system_settings: true
      };
    case 'school_admin':
      return {
        manage_teachers: true,
        manage_moderators: true,
        manage_students: true,
        manage_guardians: true,
        manage_devices: true,
        view_analytics: true,
        modify_school_settings: true,
        delete_data: true
      };
    case 'school_moderator':
      return {
        view_teachers: true,
        view_students: true,
        view_guardians: true,
        add_guests: true,
        approve_guardians: true,
        view_pickup_logs: true,
        view_basic_analytics: true,
        manage_own_profile: true
      };
    case 'teacher':
      return {
        view_own_students: true,
        sign_out_students: true,
        view_pickup_logs: true,
        manual_license_entry: true
      };
    default:
      return {};
  }
};

/**
 * Quick setup function for common user scenarios
 */
export const quickCreateUser = {
  // Create a developer user
  developer: (firebase_uid, email, name) => createUserInDatabase({
    firebase_uid,
    email,
    name,
    role: 'developer'
  }),
  
  // Create a school admin user
  schoolAdmin: (firebase_uid, email, name, school_id) => createUserInDatabase({
    firebase_uid,
    email,
    name,
    role: 'school_admin',
    school_id,
    school_ids: [school_id]
  }),
  
  // Create a school moderator user
  schoolModerator: (firebase_uid, email, name, school_id) => createUserInDatabase({
    firebase_uid,
    email,
    name,
    role: 'school_moderator',
    school_id
  }),
  
  // Create a teacher user
  teacher: (firebase_uid, email, name, school_id, employee_id, pin_code) => createUserInDatabase({
    firebase_uid,
    email,
    name,
    role: 'teacher',
    school_id,
    employee_id,
    pin_code
  })
};

// Example usage:
// 1. Get your Firebase Auth UID from the console logs when you try to login
// 2. Use one of these functions to create a user document:

// For a developer user:
// quickCreateUser.developer('your-firebase-uid', 'dev@safeline.com', 'SafeLine Developer');

// For a school admin:
// quickCreateUser.schoolAdmin('your-firebase-uid', 'admin@school.com', 'School Admin', 'school_1_id');

export default { createUserInDatabase, quickCreateUser };